import Button from '@components/Button/Button';
import { DeleteDialog } from '@components/Card2/DeleteDialog';
import { DeleteButton } from '@components/Card2/EditDialog/DeleteButton';
import Popover, {
  type PositionerOptions,
  positionBounded,
  positionViewportCentered,
} from '@components/Popover/Popover';
import { ContentSection } from '@components/Popover/PopoverLayouts';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useMedia from '@hooks/use-media';
import {
  Box,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Inline,
  StickyBottom,
  Button as UiButton,
} from '@ui-lib';
import { breakpoints, tokens } from '@ui-lib/css';
import { motion } from 'motion/react';
import { type FormEvent, memo } from 'react';

import legacyStyles from '../EditCardPopover/EditCardPopover.module.scss';

// Convert the rem-valued design-system space token into the pixel number the
// popover positioner expects. Assumes the standard 16px root font-size used
// throughout the app.
const SPACE_LARGE_PX = parseFloat(tokens.space.large) * 16;

type FieldType = 'field' | 'picture';

export type EditCardDialogPopoverProps = {
  title: string;
  adaptive?: boolean;
  drawFocus?: boolean;
  fieldType: FieldType;
  canDelete?: boolean;
  canSubmit?: boolean;
  saving?: boolean;
  cancelLabel?: string;
  saveLabel?: string;
  onCancel: () => void;
  onClose: () => void;
  onDelete?: () => void;
  onDeletePress?: () => void;
  onSubmit?: (e: FormEvent) => void;
  onCancelDeleteWarning?: () => void;
  onCancelUnlink?: () => void;
  onConfirmUnlink?: () => void;
  positionDeps?: React.DependencyList;
  positionerOptions?: PositionerOptions;
  preventCloseOnClickOutsidePopover?: boolean;
  show: boolean;
  showDeleteWarning?: boolean;
  showUnlinkWarning?: boolean;
  targetNode: HTMLElement | undefined;
  unlinking?: boolean;
  unlinkWarningMessage?: string;
};

const UnlinkWarningContent: React.FC<{
  unlinkWarningMessage?: string;
  unlinking: boolean;
  onCancelUnlink: () => void;
  onConfirmUnlink: () => void;
}> = ({ unlinkWarningMessage, unlinking, onCancelUnlink, onConfirmUnlink }) => (
  <Dialog>
    <DialogContent>
      <ContentSection>
        <motion.div
          className={legacyStyles['action-view']}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className={legacyStyles['notification-icon']}>
            <FontAwesomeIcon icon={faExclamationCircle} />
          </div>
          <h3 className={legacyStyles['action-view-heading']}>Are you sure?</h3>
          {unlinkWarningMessage && (
            <p className={legacyStyles['action-view-explanation']}>{unlinkWarningMessage}</p>
          )}
          <div className={legacyStyles['action-view-action-btns']}>
            <Button variant="subtle" size="medium" onClick={onCancelUnlink}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              type="primary"
              onClick={onConfirmUnlink}
              loading={unlinking}
            >
              Save and unlink
            </Button>
          </div>
        </motion.div>
      </ContentSection>
    </DialogContent>
  </Dialog>
);

const EditCardDialogPopover: React.FC<React.PropsWithChildren<EditCardDialogPopoverProps>> = memo(
  ({
    title,
    adaptive,
    drawFocus = false,
    fieldType,
    canDelete = false,
    canSubmit = false,
    saving = false,
    cancelLabel = 'Cancel',
    saveLabel = 'Save',
    children,
    onCancel,
    onClose,
    onCancelDeleteWarning,
    onCancelUnlink,
    onConfirmUnlink,
    onDelete,
    onDeletePress,
    onSubmit,
    positionDeps = [],
    positionerOptions,
    preventCloseOnClickOutsidePopover,
    show,
    showDeleteWarning,
    showUnlinkWarning,
    targetNode,
    unlinking = false,
    unlinkWarningMessage,
  }) => {
    // Below tablet the customize-card preview (the popover's usual anchor) is
    // hidden, so anchoring would position the dialog against a zero-size rect
    // in the top-left corner. Center it in the viewport as a fixed modal
    // instead so the field/image/layout values remain reachable on mobile.
    const isMobile = useMedia([`(max-width: ${breakpoints.tablet - 1}px)`], [true], false);

    return (
      <Popover
        positioner={
          isMobile
            ? positionViewportCentered()
            : positionBounded(
                positionerOptions || {
                  offsetX: SPACE_LARGE_PX,
                  xPreference: 'right',
                  alignY: 'cover',
                  adaptive: adaptive ?? true,
                  bindToViewport: true,
                },
              )
        }
        fixed={isMobile}
        targetNode={targetNode}
        show={show}
        onClose={onClose}
        applyModalStyle={false}
        preventOutsideInteraction={isMobile}
        preventCloseOnClickOutsidePopover={preventCloseOnClickOutsidePopover}
        positionDeps={[isMobile, showDeleteWarning, showUnlinkWarning, ...positionDeps]}
        drawFocus={drawFocus}
      >
        <div style={{ width: 500, maxWidth: 'calc(100vw - 32px)' }}>
          {showDeleteWarning ? (
            <DeleteDialog
              title={title}
              onClose={onClose}
              onCancel={() => onCancelDeleteWarning?.()}
              onSubmit={(event) => {
                event.preventDefault();
                onDelete?.();
              }}
            />
          ) : showUnlinkWarning && onCancelUnlink && onConfirmUnlink ? (
            <UnlinkWarningContent
              unlinkWarningMessage={unlinkWarningMessage}
              unlinking={unlinking}
              onCancelUnlink={onCancelUnlink}
              onConfirmUnlink={onConfirmUnlink}
            />
          ) : onSubmit ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                onSubmit(event);
              }}
            >
              <Dialog>
                <DialogHeader title={title} onClose={onClose} />
                <Box
                  paddingX="medium"
                  paddingY="large"
                  style={{ maxHeight: 'calc(60vh - 136px)', overflowY: 'auto' }}
                >
                  {children}
                </Box>
                <StickyBottom>
                  <DialogFooter>
                    <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                      {canDelete && onDeletePress ? (
                        <DeleteButton onClick={onDeletePress} />
                      ) : (
                        <span />
                      )}
                      <Inline gap="xsmall">
                        <UiButton variant="outline" type="button" onClick={onCancel}>
                          {cancelLabel}
                        </UiButton>
                        <UiButton
                          variant="solid"
                          type="submit"
                          disabled={!canSubmit}
                          loading={saving}
                        >
                          {saveLabel}
                        </UiButton>
                      </Inline>
                    </Box>
                  </DialogFooter>
                </StickyBottom>
              </Dialog>
            </form>
          ) : (
            <Dialog>
              <DialogHeader title={title} onClose={onClose} />
              <DialogContent style={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
                {children}
              </DialogContent>
              <StickyBottom>
                <DialogFooter>
                  <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                    {canDelete && onDeletePress ? (
                      <DeleteButton onClick={onDeletePress} />
                    ) : (
                      <span />
                    )}
                    <Inline gap="xsmall">
                      <UiButton variant="outline" type="button" onClick={onCancel}>
                        {cancelLabel}
                      </UiButton>
                      <UiButton
                        variant="solid"
                        type="button"
                        disabled={!canSubmit}
                        loading={saving}
                        onClick={() => {
                          /* save handled by submit-less callers via custom click */
                        }}
                      >
                        {saveLabel}
                      </UiButton>
                    </Inline>
                  </Box>
                </DialogFooter>
              </StickyBottom>
            </Dialog>
          )}
        </div>
      </Popover>
    );
  },
);

EditCardDialogPopover.displayName = 'EditCardDialogPopover';

export default EditCardDialogPopover;
